import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  Stack
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Refresh as RefreshIcon,
  AccountBalanceWallet as WalletIcon,
  Payments as PaymentsIcon,
  MoneyOff as MoneyOffIcon,
  AttachMoney as MoneyIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  HourglassTop as PendingIcon,
  Check as ApproveIcon,
  Close as RejectIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { apiService } from '../../services/api';

// Tipos locales
interface TechnicianFinanceData {
  id: string;
  name: string;
  email: string;
  cashOwedToCompany: number;
  commissionsOwedByCompany: number;
  netBalance: number;
  installationsThisMonth: number;
  thisMonth: {
    cashCollected: number;
    cashDelivered: number;
    commissionsEarned: number;
    commissionsPaid: number;
  };
  lastTransaction: string | null;
}

interface FinanceSummary {
  technicians: TechnicianFinanceData[];
  totals: {
    totalCashWithTechnicians: number;
    totalCommissionsPending: number;
  };
}

const TechnicianFinancePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<FinanceSummary | null>(null);

  // Estado para diálogos
  const [cashDeliveryDialog, setCashDeliveryDialog] = useState<{
    open: boolean;
    technician: TechnicianFinanceData | null;
  }>({ open: false, technician: null });

  const [payCommissionDialog, setPayCommissionDialog] = useState<{
    open: boolean;
    technician: TechnicianFinanceData | null;
  }>({ open: false, technician: null });

  const [cashCollectionDialog, setCashCollectionDialog] = useState<{
    open: boolean;
    technician: TechnicianFinanceData | null;
  }>({ open: false, technician: null });

  // Formularios
  const [cashDeliveryForm, setCashDeliveryForm] = useState({
    amount: '',
    paymentReference: '',
    notes: ''
  });

  const [payCommissionForm, setPayCommissionForm] = useState({
    amount: '',
    paymentMethod: 'bank_transfer' as 'cash' | 'bank_transfer' | 'check',
    paymentReference: '',
    notes: ''
  });

  const [cashCollectionForm, setCashCollectionForm] = useState({
    amount: '',
    notes: ''
  });

  const [submitting, setSubmitting] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getTechnicianFinanceSummary();
      if (response.success && response.data) {
        setSummary(response.data);
      } else {
        setError('Error al cargar datos de finanzas');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  // Entregas que los técnicos declararon y todavía no bajan de su balance.
  const [pendingDeliveries, setPendingDeliveries] = useState<any[]>([]);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const fetchPendingDeliveries = useCallback(async () => {
    try {
      const res: any = await apiService.getPendingDeliveries();
      setPendingDeliveries(res.data?.deliveries || []);
    } catch (err) {
      console.error('Error fetching pending deliveries:', err);
    }
  }, []);

  const handleResolveDelivery = async (id: string, approve: boolean) => {
    // El rechazo pide motivo: el técnico lo ve en su panel y sabe qué reclamar.
    let reason: string | undefined;
    if (!approve) {
      const escrito = window.prompt('¿Por qué no se aprueba? El técnico lo va a ver.');
      if (escrito === null) return;
      reason = escrito;
    }
    setResolvingId(id);
    try {
      const res: any = await apiService.resolveDelivery(id, approve, reason);
      if (!res?.success) {
        setError(res?.message || 'No se pudo procesar la entrega');
      }
      await Promise.all([fetchSummary(), fetchPendingDeliveries()]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'No se pudo procesar la entrega');
    } finally {
      setResolvingId(null);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchPendingDeliveries();
  }, [fetchSummary, fetchPendingDeliveries]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleCashDelivery = async () => {
    if (!cashDeliveryDialog.technician) return;

    setSubmitting(true);
    try {
      await apiService.registerCashDelivery({
        technicianId: cashDeliveryDialog.technician.id,
        amount: parseFloat(cashDeliveryForm.amount),
        paymentReference: cashDeliveryForm.paymentReference || undefined,
        notes: cashDeliveryForm.notes || undefined
      });
      setCashDeliveryDialog({ open: false, technician: null });
      setCashDeliveryForm({ amount: '', paymentReference: '', notes: '' });
      fetchSummary();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al registrar entrega');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayCommission = async () => {
    if (!payCommissionDialog.technician) return;

    setSubmitting(true);
    try {
      await apiService.payTechnicianCommission({
        technicianId: payCommissionDialog.technician.id,
        amount: parseFloat(payCommissionForm.amount),
        paymentMethod: payCommissionForm.paymentMethod,
        paymentReference: payCommissionForm.paymentReference || undefined,
        notes: payCommissionForm.notes || undefined
      });
      setPayCommissionDialog({ open: false, technician: null });
      setPayCommissionForm({ amount: '', paymentMethod: 'bank_transfer', paymentReference: '', notes: '' });
      fetchSummary();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al pagar comisión');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCashCollection = async () => {
    if (!cashCollectionDialog.technician) return;

    setSubmitting(true);
    try {
      await apiService.registerCashCollection({
        technicianId: cashCollectionDialog.technician.id,
        amount: parseFloat(cashCollectionForm.amount),
        notes: cashCollectionForm.notes || undefined
      });
      setCashCollectionDialog({ open: false, technician: null });
      setCashCollectionForm({ amount: '', notes: '' });
      fetchSummary();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al registrar cobro');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Finanzas de Técnicos
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchSummary}
          disabled={loading}
        >
          Actualizar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Entregas declaradas por los técnicos. Va arriba de todo porque hasta que no se
          apruebe, el balance del técnico sigue mostrando un efectivo que él ya no tiene. */}
      {pendingDeliveries.length > 0 && (
        <Paper sx={{ mb: 4, p: { xs: 1.5, sm: 2 }, borderLeft: '4px solid', borderColor: 'warning.main' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <PendingIcon color="warning" />
            Entregas por aprobar ({pendingDeliveries.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            El monto es lo que recibiste en mano: el técnico ya se cobró su comisión de ese mismo
            dinero. Al aprobar se cierran las dos cosas — su efectivo y la comisión — con la fecha
            que él registró, no con la de hoy.
          </Typography>
          <Stack spacing={1.5}>
            {pendingDeliveries.map((d: any) => (
              <Box
                key={d._id}
                sx={{
                  display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5,
                  p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" fontWeight={600}>
                    {d.technicianId?.name || 'Técnico'} — RD$
                    {((d.amount || 0) - (d.commissionSettled || 0)).toLocaleString('es-DO')}
                  </Typography>
                  {(d.commissionSettled || 0) > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Baja RD${(d.amount || 0).toLocaleString('es-DO')} de su efectivo, de los que se
                      cobró RD${(d.commissionSettled || 0).toLocaleString('es-DO')} de comisión.
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Entregado a <strong>{d.deliveredToName}</strong> el{' '}
                    {format(new Date(d.transactionDate), "d 'de' MMMM, h:mm a", { locale: es })}
                  </Typography>
                  {d.notes && (
                    <Typography variant="caption" color="text.secondary">{d.notes}</Typography>
                  )}
                  <Typography variant="caption" display="block" color="text.secondary">
                    Su efectivo hoy: RD$
                    {(d.technicianId?.technicianFinancials?.cashOwedToCompany || 0).toLocaleString('es-DO')}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button
                    variant="contained" color="success" startIcon={<ApproveIcon />}
                    disabled={resolvingId === d._id}
                    onClick={() => handleResolveDelivery(d._id, true)}
                    fullWidth
                  >
                    Aprobar
                  </Button>
                  <Button
                    variant="outlined" color="error" startIcon={<RejectIcon />}
                    disabled={resolvingId === d._id}
                    onClick={() => handleResolveDelivery(d._id, false)}
                    fullWidth
                  >
                    Rechazar
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Tarjetas de resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Efectivo con Técnicos
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {formatCurrency(summary?.totals.totalCashWithTechnicians || 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Pendiente de entregar
                  </Typography>
                </Box>
                <WalletIcon sx={{ fontSize: 48, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Comisiones Pendientes
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {formatCurrency(summary?.totals.totalCommissionsPending || 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Por pagar a técnicos
                  </Typography>
                </Box>
                <PaymentsIcon sx={{ fontSize: 48, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{
            bgcolor: (summary?.totals.totalCommissionsPending || 0) - (summary?.totals.totalCashWithTechnicians || 0) >= 0
              ? 'info.light'
              : 'error.light',
            color: 'white'
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {(summary?.totals.totalCommissionsPending || 0) - (summary?.totals.totalCashWithTechnicians || 0) > 0
                      ? 'Driports debe a técnicos'
                      : 'Técnicos tienen que entregar'}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {formatCurrency(Math.abs(
                      (summary?.totals.totalCommissionsPending || 0) -
                      (summary?.totals.totalCashWithTechnicians || 0)
                    ))}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    efectivo en su mano − comisiones pendientes
                  </Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 48, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla de técnicos */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Balance por Técnico
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Técnico</strong></TableCell>
                  <TableCell align="right"><strong>Debe a Empresa</strong></TableCell>
                  <TableCell align="right"><strong>Comisiones Pendientes</strong></TableCell>
                  <TableCell align="right"><strong>A entregar</strong></TableCell>
                  <TableCell align="center"><strong>Instalaciones (Mes)</strong></TableCell>
                  <TableCell align="center"><strong>Última Transacción</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary?.technicians.map((tech) => (
                  <TableRow key={tech.id} hover>
                    <TableCell>
                      <Box>
                        <Typography fontWeight="medium">{tech.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tech.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {tech.cashOwedToCompany > 0 ? (
                        <Chip
                          icon={<TrendingDownIcon />}
                          label={formatCurrency(tech.cashOwedToCompany)}
                          color="warning"
                          size="small"
                        />
                      ) : (
                        <Typography color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {tech.commissionsOwedByCompany > 0 ? (
                        <Chip
                          icon={<TrendingUpIcon />}
                          label={formatCurrency(tech.commissionsOwedByCompany)}
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Typography color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {/* Efectivo − comisiones. Si sale al revés, es Driports quien le debe. */}
                      <Typography
                        fontWeight="bold"
                        color={tech.netBalance > 0 ? 'info.main' : tech.netBalance < 0 ? 'error.main' : 'text.secondary'}
                      >
                        {formatCurrency(Math.max(0, -tech.netBalance))}
                      </Typography>
                      {tech.netBalance > 0 && (
                        <Typography variant="caption" color="info.main">Driports le debe {formatCurrency(tech.netBalance)}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={tech.installationsThisMonth}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {tech.lastTransaction ? (
                        <Typography variant="caption">
                          {format(new Date(tech.lastTransaction), 'dd/MM/yyyy HH:mm', { locale: es })}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Sin transacciones
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Ver detalle">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/technicians/${tech.id}/balance`)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Registrar cobro en efectivo">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => {
                              setCashCollectionDialog({ open: true, technician: tech });
                              setCashCollectionForm({ amount: '', notes: '' });
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {tech.cashOwedToCompany > 0 && (
                          <Tooltip title="Registrar entrega de efectivo">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => {
                                setCashDeliveryDialog({ open: true, technician: tech });
                                setCashDeliveryForm({
                                  amount: tech.cashOwedToCompany.toString(),
                                  paymentReference: '',
                                  notes: ''
                                });
                              }}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {tech.commissionsOwedByCompany > 0 && (
                          <Tooltip title="Pagar comisiones">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => {
                                setPayCommissionDialog({ open: true, technician: tech });
                                setPayCommissionForm({
                                  amount: tech.commissionsOwedByCompany.toString(),
                                  paymentMethod: 'bank_transfer',
                                  paymentReference: '',
                                  notes: ''
                                });
                              }}
                            >
                              <PaymentsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {(!summary?.technicians || summary.technicians.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No hay técnicos registrados
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog: Registrar cobro en efectivo */}
      <Dialog
        open={cashCollectionDialog.open}
        onClose={() => setCashCollectionDialog({ open: false, technician: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Registrar Cobro en Efectivo
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              El técnico <strong>{cashCollectionDialog.technician?.name}</strong> recibió efectivo de un cliente.
              Este monto se sumará a lo que debe entregar a la empresa.
            </Alert>
            <TextField
              fullWidth
              label="Monto"
              type="number"
              value={cashCollectionForm.amount}
              onChange={(e) => setCashCollectionForm({ ...cashCollectionForm, amount: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">DOP</InputAdornment>
              }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Notas (opcional)"
              multiline
              rows={2}
              value={cashCollectionForm.notes}
              onChange={(e) => setCashCollectionForm({ ...cashCollectionForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCashCollectionDialog({ open: false, technician: null })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCashCollection}
            disabled={submitting || !cashCollectionForm.amount || parseFloat(cashCollectionForm.amount) <= 0}
          >
            {submitting ? <CircularProgress size={24} /> : 'Registrar Cobro'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Registrar entrega de efectivo */}
      <Dialog
        open={cashDeliveryDialog.open}
        onClose={() => setCashDeliveryDialog({ open: false, technician: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Registrar Entrega de Efectivo
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              El técnico <strong>{cashDeliveryDialog.technician?.name}</strong> entrega efectivo a la empresa.
              Actualmente debe: <strong>{formatCurrency(cashDeliveryDialog.technician?.cashOwedToCompany || 0)}</strong>
            </Alert>
            <TextField
              fullWidth
              label="Monto a entregar"
              type="number"
              value={cashDeliveryForm.amount}
              onChange={(e) => setCashDeliveryForm({ ...cashDeliveryForm, amount: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">DOP</InputAdornment>
              }}
              helperText={`Máximo: ${formatCurrency(cashDeliveryDialog.technician?.cashOwedToCompany || 0)}`}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Referencia/Recibo (opcional)"
              value={cashDeliveryForm.paymentReference}
              onChange={(e) => setCashDeliveryForm({ ...cashDeliveryForm, paymentReference: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Notas (opcional)"
              multiline
              rows={2}
              value={cashDeliveryForm.notes}
              onChange={(e) => setCashDeliveryForm({ ...cashDeliveryForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCashDeliveryDialog({ open: false, technician: null })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCashDelivery}
            disabled={
              submitting ||
              !cashDeliveryForm.amount ||
              parseFloat(cashDeliveryForm.amount) <= 0 ||
              parseFloat(cashDeliveryForm.amount) > (cashDeliveryDialog.technician?.cashOwedToCompany || 0)
            }
          >
            {submitting ? <CircularProgress size={24} /> : 'Registrar Entrega'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Pagar comisiones */}
      <Dialog
        open={payCommissionDialog.open}
        onClose={() => setPayCommissionDialog({ open: false, technician: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Pagar Comisiones al Técnico
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Comisiones pendientes de <strong>{payCommissionDialog.technician?.name}</strong>:
              <strong> {formatCurrency(payCommissionDialog.technician?.commissionsOwedByCompany || 0)}</strong>
            </Alert>
            <TextField
              fullWidth
              label="Monto a pagar"
              type="number"
              value={payCommissionForm.amount}
              onChange={(e) => setPayCommissionForm({ ...payCommissionForm, amount: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">DOP</InputAdornment>
              }}
              helperText={`Máximo: ${formatCurrency(payCommissionDialog.technician?.commissionsOwedByCompany || 0)}`}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Método de Pago</InputLabel>
              <Select
                value={payCommissionForm.paymentMethod}
                label="Método de Pago"
                onChange={(e) => setPayCommissionForm({
                  ...payCommissionForm,
                  paymentMethod: e.target.value as 'cash' | 'bank_transfer' | 'check'
                })}
              >
                <MenuItem value="cash">Efectivo</MenuItem>
                <MenuItem value="bank_transfer">Transferencia Bancaria</MenuItem>
                <MenuItem value="check">Cheque</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Referencia de Pago (opcional)"
              value={payCommissionForm.paymentReference}
              onChange={(e) => setPayCommissionForm({ ...payCommissionForm, paymentReference: e.target.value })}
              placeholder="Ej: Transferencia #123456"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Notas (opcional)"
              multiline
              rows={2}
              value={payCommissionForm.notes}
              onChange={(e) => setPayCommissionForm({ ...payCommissionForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayCommissionDialog({ open: false, technician: null })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePayCommission}
            disabled={
              submitting ||
              !payCommissionForm.amount ||
              parseFloat(payCommissionForm.amount) <= 0 ||
              parseFloat(payCommissionForm.amount) > (payCommissionDialog.technician?.commissionsOwedByCompany || 0)
            }
          >
            {submitting ? <CircularProgress size={24} /> : 'Pagar Comisión'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TechnicianFinancePage;
